/**
 * Protected Route Component with Role-Based Access
 *
 * Wraps routes that require authentication and/or specific roles.
 * Redirects to login if not authenticated.
 * Shows AccessDenied if authenticated but lacking required roles.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useRBAC } from '@/hooks/useRBAC'
import { AccessDenied } from './AccessDenied'
import type { RoleName } from '@/lib/roles'

interface ProtectedRouteProps {
  /** Required roles - user needs ANY of these roles (OR logic) */
  requiredRoles?: RoleName[]
  /** Custom redirect path for unauthenticated users */
  redirectTo?: string
  /** Children to render (used when not as a route element) */
  children?: React.ReactNode
}

export function ProtectedRoute({
  requiredRoles,
  redirectTo = '/login',
  children,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const { hasAnyRole } = useRBAC()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-eventus-teal" />
          <p className="text-white/80 text-sm">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Check role-based access if roles are required
  if (requiredRoles && requiredRoles.length > 0) {
    const hasAccess = hasAnyRole(...requiredRoles)

    if (!hasAccess) {
      return <AccessDenied requiredRoles={requiredRoles} />
    }
  }

  // Render children or Outlet for nested routes
  return children ? <>{children}</> : <Outlet />
}
